import React, { useEffect, useState, useMemo, useCallback, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import SVG from 'react-inlinesvg';
import * as Tooltip from '@radix-ui/react-tooltip';
import * as Popover from '@radix-ui/react-popover';
import Modal from 'react-modal';

import { getKYCStatusByAPI } from '@utils/kyc-sumsub-util';
import Button from '@components/atoms/button/button';
import { userAction, kycSumsubAction } from '@redux/action';
import { KYC_TYPES_STATUS } from '@utils/constants';
import { useSumsub } from '@hooks/kyc-sumsub-hook';

import styles from './kyc-sumsub.module.scss';
const Popup = lazy(() => import('@components/organisms/popup/popup'));
const PopupBody = lazy(() => import('@components/organisms/popup/popup').then((module) => ({ default: module.PopupBody })));

const mobileCustomModalStyles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
    content: {
        margin: 0,
        position: 'absolute',
        inset: 0,
        border: 'none',
        background: 'white',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '0',
        outline: 'none',
        padding: '0',
        minWidth: '100%',
    },
};

const desktopCustomModalStyles = {
    overlay: {
        position: 'fixed',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
    },
    content: {
        inset: '0',
        position: 'absolute',
        border: 'none',
        background: 'white',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '0',
        outline: 'none',
        width: '80%',
        maxWidth: '1600px',
        height: 'fit-content',
    },
};

const KycSumsub = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['kyc']);
    const { memberKYCSumsubInfo, isDeposited } = useSelector((state) => state.user);

    const [filteredKycData, setFilteredKycData] = useState([]);
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
    const [isOpenSumsubModal, setIsOpenSumsubModal] = useState(false);
    const { fetchMemberKYCSumsubInfo } = useSumsub();

    useEffect(() => {
        const applicants = memberKYCSumsubInfo?.applicants || [];
        const hasLevel = (level) => applicants.some((item) => item.applicantLevel === level);
        const isL0ApprovedOrRejected = applicants.some((item) => item.applicantLevel === 'L0' && ['A', 'R'].includes(item.applicantStatus));
        const isL0ActivePending = applicants.some((item) => item.applicantLevel === 'L0' && item.isActiveLevel && !['A', 'R'].includes(item.applicantStatus));

        const filteredData = applicants.filter((step) => {
            const { applicantLevel } = step;

            // Exclude L1 if L0 or L2 exists
            if (hasLevel('L1') && (hasLevel('L0') || hasLevel('L2'))) {
                return applicantLevel !== 'L1';
            }

            // Exclude L0 if L0 is approved/rejected or if L0 and L2 both exist
            if (hasLevel('L0') && (isL0ApprovedOrRejected || hasLevel('L2'))) {
                return applicantLevel !== 'L0';
            }

            // Include L0 if active and not approved/rejected
            if (hasLevel('L0') && isL0ActivePending) {
                return true;
            }

            return true;
        });

        const sortedData = [...filteredData].sort((a, b) => {
            if (a.applicantLevel === 'L2') return -1;
            if (b.applicantLevel === 'L2') return 1;
            return 0;
        });

        const fallback = applicants.some((item) => item.applicantLevel === 'L0' && ['DR', 'A', 'R'].includes(item.applicantStatus))
            ? [{ applicantLevel: 'L2', applicantStatus: 'DR' }]
            : [{ applicantLevel: 'L1', applicantStatus: 'DR' }];

        setFilteredKycData(sortedData.length > 0 ? sortedData : fallback);
    }, [memberKYCSumsubInfo.applicants]);

    useEffect(() => {
        const fetchDepositedStatus = async () => {
            if (isDataFetched) return;

            if (isDeposited) {
                setIsDataFetched(true);
                return;
            }

            try {
                const result = await window.SPL_Member.getFirstTimeDepositStatus();
                setIsDataFetched(true);
                if (result) {
                    dispatch(userAction.setDepositStatus(result));
                }
            } catch (error) {
                console.error('Failed to fetch deposit status:', error);
            }
        };

        fetchDepositedStatus();
    }, [isDataFetched, isDeposited, dispatch]);

    // Further filter the KYC levels so that if both L1 and L2 exist, only L2 is shown.
    const filteredKycForLevels = useMemo(() => {
        const hasL1 = filteredKycData.some(({ applicantLevel }) => applicantLevel === 'L1');
        const hasL2 = filteredKycData.some(({ applicantLevel }) => applicantLevel === 'L2');

        // If both L1 and L2 exist, only show L2
        return hasL1 && hasL2 ? filteredKycData.filter(({ applicantLevel }) => applicantLevel === 'L2') : filteredKycData;
    }, [filteredKycData]);

    const toggleModal = useCallback(
        (modalType) => {
            if (modalType === 'sumsub') {
                setIsOpenSumsubModal((prevState) => !prevState);
                fetchMemberKYCSumsubInfo();
            } else if (modalType === 'error') {
                setIsOpenErrorModal((prevState) => !prevState);
            }
        },
        [fetchMemberKYCSumsubInfo]
    );

    return (
        <>
            <ErrorPopUp isOpenErrorModal={isOpenErrorModal} toggleModal={toggleModal} />

            <SumsubPopup isOpenSumsubModal={isOpenSumsubModal} toggleModal={toggleModal} />

            <div className={styles.container}>
                <div className={styles.titleWrapper}>
                    <div className={`${styles.title} fs-18 fw-700`}>{t('kyc:knowYourCustomersFullName')}</div>
                    <div className={`${styles.subTitle}`}>{t('kyc:tooltipProcessVerifying')}</div>
                </div>

                <div className={styles.documentLevelContainer}>
                    {(filteredKycForLevels || []).map((item, index) => (
                        <KycLevel
                            key={item?.id}
                            index={index}
                            applicants={filteredKycForLevels}
                            data={item}
                            setIsOpenErrorModal={setIsOpenErrorModal}
                            setIsOpenSumsubModal={setIsOpenSumsubModal}
                        />
                    ))}
                </div>

                <div className={styles.notice}>
                    <SVG src='/public/html/images/common_img/icon-important.svg' />
                    <div className={`${styles.message} fs-12`}>{t('kyc:userWithDepositRecordAllowSubmitKYC2')}</div>
                </div>
            </div>
        </>
    );
};

export default KycSumsub;

const KycLevel = ({ index, applicants, data, setIsOpenErrorModal, setIsOpenSumsubModal }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(['kyc']);
    const screen = useSelector((state) => state.screen);
    const { isDeposited } = useSelector((state) => state.user);
    const { isLoading } = useSelector((state) => state.kycSumsubReducer);
    const isMobile = screen?.viewType === 'mobile';

    const hasL0 = useMemo(() => (applicants || []).some((item) => item.applicantLevel === 'LO'), [applicants]);
    const step = useMemo(() => getKYCStatusByAPI(data), [data]);
    const { labelIcon, text, statusIcon, statusText, tooltipText, applicantLevel } = step || {};
    const { launchKYC, getAccessToken, onKycSubmitPending, onKycSubmitRejected } = useSumsub(applicantLevel, index);

    const handleStartClick = useCallback(async () => {
        dispatch(kycSumsubAction.setIsLoading(true));
        await onKycSubmitPending(step, index);

        const token = await getAccessToken();
        if (!token) {
            setIsOpenErrorModal(true);
            dispatch(kycSumsubAction.setIsLoading(false));
            await onKycSubmitRejected(step, index);
            return;
        }

        setIsOpenSumsubModal(true);
        await launchKYC(token);
        dispatch(kycSumsubAction.setIsLoading(false));
    }, [dispatch, onKycSubmitPending, onKycSubmitRejected, getAccessToken, launchKYC, setIsOpenErrorModal, setIsOpenSumsubModal, step, index]);

    const isButtonDisabled = useMemo(
        () => isLoading || [KYC_TYPES_STATUS.APPROVED, KYC_TYPES_STATUS.REJECTED, 'P'].includes(step.status) || !isDeposited || (step.applicantLevel === 'L1' && hasL0),
        [isLoading, step, isDeposited, hasL0]
    );

    return (
        <div className={styles.documentLevel}>
            <div className={styles.levelDetails}>
                <div className={styles.textContainer}>
                    <SVG src={labelIcon} />
                    <div className={`${styles.text} fs-16 fw-700`}>{t(text)}</div>
                </div>
                <div className={styles.statusContainer}>
                    <SVG src={statusIcon} />
                    {!isMobile && <div className={`${styles.text} fs-16`}>{t(statusText)}</div>}
                </div>
            </div>

            {isMobile ? <PopoverComponent tooltipText={tooltipText} /> : <TooltipComponent tooltipText={tooltipText} />}

            <Button className={styles.startButton} disabled={isButtonDisabled} onClick={() => handleStartClick()}>
                {t('global:entity.action.start', 'Start')}
            </Button>
        </div>
    );
};

const TooltipComponent = ({ tooltipText }) => {
    const { t } = useTranslation(['kyc']);
    return (
        <Tooltip.Provider delayDuration={0}>
            <Tooltip.Root>
                <Tooltip.Trigger asChild>
                    <button className={`${styles.iconButton}`}>
                        <div className={styles.tooltip}>
                            <SVG src='/public/html/images/common_img/icon-important.svg' />
                        </div>
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content sideOffset={5} side='right'>
                        <div className={styles.tooltipContainer}>
                            <div className={`${styles.tooltipText} fs-14`}>{t(tooltipText)}</div>
                        </div>
                        <Tooltip.Arrow className={styles.tooltipArrow} />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
};

const PopoverComponent = ({ tooltipText }) => {
    const { t } = useTranslation(['kyc']);
    return (
        <Popover.Root>
            <Popover.Trigger asChild>
                <button className={`${styles.iconButton}`}>
                    <div className={styles.tooltip}>
                        <SVG src='/public/html/images/common_img/icon-important.svg' />
                    </div>
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content className='PopoverContent' sideOffset={5} align='end'>
                    <div className={styles.tooltipContainer}>
                        <div className={`${styles.tooltipText} fs-14`}>{t(tooltipText)}</div>
                    </div>
                    <Popover.Arrow className={styles.tooltipArrow} />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};

const ErrorPopUp = ({ isOpenErrorModal, toggleModal }) => {
    const { t } = useTranslation(['kyc', 'googleAuthenticator']);

    return (
        <Popup isOpen={isOpenErrorModal} onRequestClose={() => toggleModal('error')} className={styles.errorPopup}>
            <PopupBody className={styles.body}>
                <SVG src='/public/html/images/kyc/icon-red-cross.svg' />
                <div className={styles.errorMessage}>{t('kyc:kycError')}</div>
                <Button onClick={() => toggleModal('error')}>{t('googleAuthenticator:googleAuthenticator.button.dismiss', 'Dismiss')}</Button>
            </PopupBody>
        </Popup>
    );
};

const SumsubPopup = ({ isOpenSumsubModal, toggleModal }) => {
    const screen = useSelector((state) => state.screen);
    return (
        <Modal
            overlayClassName='standard-popup-overlay'
            className={`${styles.sumsubKycOverlay} ${styles.sumsubModalContainer} standard-popup-modal-container appDownload-popup-modal-container`}
            isOpen={isOpenSumsubModal}
            onRequestClose={() => toggleModal('sumsub')}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
            style={screen?.viewType !== 'mobile' ? desktopCustomModalStyles : mobileCustomModalStyles}
        >
            <div className={`${styles.sumsubKyc}`}>
                <div style={{ position: 'relative' }}>
                    <div className={`${styles.modalCloseButton}`} onClick={() => toggleModal('sumsub')}>
                        <SVG src={`/public/html/images/common_img/icon-cross.svg`} />
                    </div>
                    <div className={`${styles.modalContent} modal-content`} id='sumsub-websdk-container' style={{ background: 'none' }} />
                </div>
            </div>
        </Modal>
    );
};
